/**
 * Google Sheets API v4 + Drive API thin fetch wrapper.
 *
 * - Reads access token from `@/lib/auth/session` (the single source of truth).
 *   Throws `MissingTokenError` when absent.
 * - On 401 from Sheets/Drive, calls the configured `silentRefresh()` callback
 *   exactly once and retries; if it still fails, surfaces the error.
 * - Token must look like a non-empty opaque string with no CR/LF — otherwise
 *   we refuse to construct an `Authorization` header, since header injection
 *   via crafted token values would let a compromised token attacker tunnel
 *   arbitrary headers into upstream requests.
 */

import { getToken as getSessionToken } from '@/lib/auth/session';
import { InvalidTokenFormatError, type MochiaError } from '@/lib/errors';

export { InvalidTokenFormatError };

const SHEETS_BASE = 'https://sheets.googleapis.com/v4/spreadsheets';
const DRIVE_FILES = 'https://www.googleapis.com/drive/v3/files';

export class MissingTokenError extends Error implements MochiaError {
  readonly hint: string;
  constructor() {
    super('No Google access token in sessionStorage');
    this.name = 'MissingTokenError';
    this.hint =
      '請點 Google 登入按鈕重新登入。若反覆出現，檢查 .env.local 的 VITE_GOOGLE_CLIENT_ID 是否設定，並重啟 Vite。';
  }
}

export class SheetsApiError extends Error {
  readonly status: number;
  readonly body: unknown;
  constructor(status: number, message: string, body: unknown) {
    super(message);
    this.name = 'SheetsApiError';
    this.status = status;
    this.body = body;
  }
}

export interface SheetsClientOptions {
  /** Override the token source (used in tests). Defaults to sessionStorage. */
  readonly getAccessToken?: () => string | null | undefined;
  /** Called when Sheets returns 401; should populate a fresh token. */
  readonly silentRefresh?: () => Promise<void>;
  /** Override fetch (used in tests). */
  readonly fetchImpl?: typeof fetch;
}

export interface ValueRange {
  readonly range?: string;
  readonly majorDimension?: 'ROWS' | 'COLUMNS';
  readonly values?: string[][];
}

export interface BatchUpdateRequest {
  readonly requests: ReadonlyArray<Record<string, unknown>>;
}

export interface BatchGetResponse {
  readonly spreadsheetId?: string;
  readonly valueRanges?: ReadonlyArray<ValueRange>;
}

export interface BatchUpdateResponse {
  readonly spreadsheetId?: string;
  readonly replies?: ReadonlyArray<Record<string, unknown>>;
}

export interface SpreadsheetCreateProps {
  readonly properties?: { readonly title?: string };
  readonly sheets?: ReadonlyArray<{
    readonly properties: { readonly title: string; readonly sheetId?: number };
  }>;
}

export interface SpreadsheetCreateResponse {
  readonly spreadsheetId: string;
  readonly properties?: { readonly title?: string };
  readonly sheets?: ReadonlyArray<{
    readonly properties?: { readonly sheetId?: number; readonly title?: string };
  }>;
}

export interface DriveFile {
  readonly id: string;
  readonly name: string;
  readonly mimeType?: string;
}

export interface DriveFilesListResponse {
  readonly files: ReadonlyArray<DriveFile>;
  readonly nextPageToken?: string;
}

export interface SheetsClient {
  valuesGet(spreadsheetId: string, range: string): Promise<ValueRange>;
  /**
   * Reads multiple ranges in one HTTP round-trip via Sheets API
   * `values:batchGet`. `valueRanges` is returned in the same order as the
   * requested `ranges` (Sheets API contract), so callers may safely index
   * by position instead of matching on `range` strings.
   */
  valuesBatchGet(spreadsheetId: string, ranges: readonly string[]): Promise<BatchGetResponse>;
  valuesAppend(
    spreadsheetId: string,
    range: string,
    values: string[][],
  ): Promise<{ updates?: { updatedRange?: string } }>;
  valuesUpdate(spreadsheetId: string, range: string, values: string[][]): Promise<ValueRange>;
  valuesBatchUpdate(spreadsheetId: string, body: BatchUpdateRequest): Promise<BatchUpdateResponse>;
  spreadsheetsBatchUpdate(
    spreadsheetId: string,
    body: BatchUpdateRequest,
  ): Promise<BatchUpdateResponse>;
  spreadsheetsCreate(props: SpreadsheetCreateProps): Promise<SpreadsheetCreateResponse>;
  driveFilesList(query: string): Promise<DriveFilesListResponse>;
}

function defaultGetToken(): string | null {
  // Single source of truth: the session module. No legacy / fallback paths;
  // duplicating a sessionStorage key was the root cause of audit finding C-1.
  return getSessionToken();
}

function safeAuthHeader(token: string): string {
  // Reject anything that could break the HTTP header line (header injection)
  // or smell wrong. Real Google access tokens are opaque base64-ish strings,
  // typically <= 2048 chars, with no whitespace.
  if (!token) throw new MissingTokenError();
  if (/[\r\n\0]/.test(token) || /\s/.test(token)) {
    // Use a dedicated 400-status error so callers can route this as a real
    // client-side validation failure instead of a network error (status 0).
    throw new InvalidTokenFormatError(
      'Refusing to build Authorization header: token has whitespace or control characters',
    );
  }
  return `Bearer ${token}`;
}

async function parseError(res: Response): Promise<{ message: string; body: unknown }> {
  const text = await res.text().catch(() => '');
  let body: unknown = text;
  try {
    body = text ? JSON.parse(text) : null;
  } catch {
    /* leave body as text */
  }
  const apiMsg =
    body && typeof body === 'object' && 'error' in body
      ? (body as { error?: { message?: string } }).error?.message
      : undefined;
  return { message: apiMsg ?? `${res.status} ${res.statusText}`, body };
}

export function createSheetsClient(options: SheetsClientOptions = {}): SheetsClient {
  const getToken = options.getAccessToken ?? defaultGetToken;
  const silentRefresh = options.silentRefresh;
  const fetchImpl: typeof fetch =
    options.fetchImpl ??
    (typeof fetch !== 'undefined'
      ? fetch.bind(globalThis)
      : (() => {
          throw new Error('No fetch impl available');
        })());

  async function authedFetch(
    url: string,
    init: RequestInit & { method?: string } = {},
  ): Promise<Response> {
    const doFetch = async (): Promise<Response> => {
      const token = getToken();
      if (!token) throw new MissingTokenError();
      const headers = new Headers(init.headers ?? {});
      headers.set('Authorization', safeAuthHeader(token));
      if (init.body && !headers.has('Content-Type')) {
        headers.set('Content-Type', 'application/json');
      }
      return fetchImpl(url, { ...init, headers });
    };

    let res = await doFetch();
    if (res.status === 401 && silentRefresh) {
      await silentRefresh();
      res = await doFetch();
    }
    return res;
  }

  async function jsonRequest<T>(
    url: string,
    init: RequestInit & { method?: string } = {},
  ): Promise<T> {
    const res = await authedFetch(url, init);
    if (!res.ok) {
      const { message, body } = await parseError(res);
      throw new SheetsApiError(res.status, message, body);
    }
    if (res.status === 204) return undefined as unknown as T;
    return (await res.json()) as T;
  }

  function encodeRange(range: string): string {
    return encodeURIComponent(range);
  }

  return {
    async valuesGet(spreadsheetId, range) {
      const url = `${SHEETS_BASE}/${encodeURIComponent(spreadsheetId)}/values/${encodeRange(range)}`;
      return jsonRequest<ValueRange>(url, { method: 'GET' });
    },

    async valuesBatchGet(spreadsheetId, ranges) {
      const query = ranges.map((r) => `ranges=${encodeRange(r)}`).join('&');
      const url = `${SHEETS_BASE}/${encodeURIComponent(spreadsheetId)}/values:batchGet?${query}`;
      return jsonRequest<BatchGetResponse>(url, { method: 'GET' });
    },

    async valuesAppend(spreadsheetId, range, values) {
      const url =
        `${SHEETS_BASE}/${encodeURIComponent(spreadsheetId)}/values/${encodeRange(range)}` +
        ':append?valueInputOption=RAW&insertDataOption=INSERT_ROWS';
      return jsonRequest(url, {
        method: 'POST',
        body: JSON.stringify({ values }),
      });
    },

    async valuesUpdate(spreadsheetId, range, values) {
      const url =
        `${SHEETS_BASE}/${encodeURIComponent(spreadsheetId)}/values/${encodeRange(range)}` +
        '?valueInputOption=RAW';
      return jsonRequest<ValueRange>(url, {
        method: 'PUT',
        body: JSON.stringify({ values }),
      });
    },

    async valuesBatchUpdate(spreadsheetId, body) {
      const url = `${SHEETS_BASE}/${encodeURIComponent(spreadsheetId)}/values:batchUpdate`;
      return jsonRequest<BatchUpdateResponse>(url, {
        method: 'POST',
        body: JSON.stringify(body),
      });
    },

    async spreadsheetsBatchUpdate(spreadsheetId, body) {
      const url = `${SHEETS_BASE}/${encodeURIComponent(spreadsheetId)}:batchUpdate`;
      return jsonRequest<BatchUpdateResponse>(url, {
        method: 'POST',
        body: JSON.stringify(body),
      });
    },

    async spreadsheetsCreate(props) {
      return jsonRequest<SpreadsheetCreateResponse>(SHEETS_BASE, {
        method: 'POST',
        body: JSON.stringify(props),
      });
    },

    async driveFilesList(query) {
      const params = new URLSearchParams({
        q: query,
        fields: 'files(id,name,mimeType),nextPageToken',
        spaces: 'drive',
        pageSize: '50',
      });
      const url = `${DRIVE_FILES}?${params.toString()}`;
      return jsonRequest<DriveFilesListResponse>(url, { method: 'GET' });
    },
  };
}
